if (!customElements.get('product-group')) {
  customElements.define('product-group', class ProductGroup extends HTMLElement {
    constructor() {
      super();
      this.currencySymbol = '€';
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('.product-groups__submit');
      this.errorWrapper = this.querySelector('.product-groups__errors');
      this.errorMessage = this.querySelector('.product-groups__error-msg');
      this.currencySymbol = this.dataset.currencySymbol || '€';

      this.priceWrapper = this.closest('.product-sticky-bar')?.querySelector(`[data-product-group-price-id="${this.dataset.productGroupId}"]`)
        || document.querySelector(`[data-product-group-price-id="${this.dataset.productGroupId}"]`);

      if (this.form) {
        this.initEventListeners();
        this.checkAllSoldOut();
        this.updateTotalPrice();
      }
    }

    initEventListeners() {
      this.form.addEventListener('submit', this.handleFormSubmit.bind(this), true);
      this.form.addEventListener('cart:error', this.onCartError.bind(this));

      // Regular variant inputs (exclude tirantes)
      this.querySelectorAll('.product-groups__item:not(.product-groups__tirantes) .product-groups__variant-input').forEach(input => {
        input.addEventListener('change', this.handleVariantChange.bind(this));
      });

      // Tirantes radio buttons
      this.querySelectorAll('.product-groups__tirantes .product-groups__variant-input').forEach(input => {
        input.addEventListener('change', this.handleTirantesChange.bind(this));
      });

      // Get first group product quantity input for tirantes sync
      const firstGroupItem = this.querySelector('.product-groups__item:not(.product-groups__tirantes)');
      const firstQuantityInput = firstGroupItem?.querySelector('.quantity-selector__input');

      this.querySelectorAll('.product-groups__item').forEach(item => {
        const input = item.querySelector('.quantity-selector__input');
        if (!input) return;

        const updateUI = () => {
          this.checkAllSoldOut();
          this.updateTotalPrice();
          // Sync tirantes quantity with first group product
          if (firstQuantityInput && input === firstQuantityInput) {
            this.syncTirantesQuantity(firstQuantityInput.value);
          }
        };

        input.addEventListener('change', updateUI);
        item.querySelectorAll('.quantity-selector__button').forEach(btn => {
          btn.addEventListener('click', updateUI);
        });
      });

      // Initial sync
      if (firstQuantityInput) {
        this.syncTirantesQuantity(firstQuantityInput.value);
      }
    }

    syncTirantesQuantity(quantity) {
      const tirantesQuantityInput = this.querySelector('.product-groups__tirantes-quantity');
      if (tirantesQuantityInput) {
        tirantesQuantityInput.value = quantity;
      }
    }

    handleTirantesChange(e) {
      // Update tirantes price display
      const tirantesDetails = e.target.closest('.product-groups__tirantes');
      const priceLabel = tirantesDetails?.querySelector('.product-groups__price');
      if (priceLabel && e.target.dataset.price) {
        const price = parseInt(e.target.dataset.price) || 0;
        priceLabel.textContent = this.formatMoney(price);
      }
      this.updateTotalPrice();
    }

    handleFormSubmit(e) {
      const items = this.collectItems();
      if (items.length === 0) {
        e.preventDefault();
        this.onCartError({ detail: { error: 'Please select at least one product' } });
        return;
      }
      e.stopImmediatePropagation();
      e.preventDefault();
      this.addMultipleItems(items);
    }

    collectItems() {
      const items = Array.from(this.querySelectorAll('.product-groups__item'))
        .map(item => ({
          item,
          quantityInput: item.querySelector('.quantity-selector__input'),
          variantInput: item.querySelector('.product-groups__variant-input:checked'),
          parentProductIdInput: item.querySelector('input[name="properties[_parentProductId]"]'),
          parentProductHandle: this.querySelector('input[name="properties[_parentProductHandle]"]')
        }))
        .filter(({ quantityInput, variantInput, parentProductIdInput }) => quantityInput?.value && variantInput && parentProductIdInput?.value)
        .map(({ quantityInput, variantInput, parentProductIdInput, parentProductHandle }) => ({
          id: variantInput.value,
          quantity: Math.max(0, parseInt(quantityInput.value) || 0),
          properties: {
            _parentProductId: parentProductIdInput.value,
            _parentProductHandle: parentProductHandle.value
          }
        }))
        .filter(({ quantity }) => quantity > 0);

      // Handle tirantes
      const tirantesYesInput = this.querySelector('.product-groups__tirantes input[data-tirantes="yes"]:checked');
      if (tirantesYesInput && tirantesYesInput.value) {
        const tirantesQuantityInput = this.querySelector('.product-groups__tirantes-quantity');
        const parentProductIdInput = this.querySelector('input[name="properties[_parentProductId]"]');
        const parentProductHandle = this.querySelector('input[name="properties[_parentProductHandle]"]');
        const tirantesProductInput = this.querySelector('input[name="properties[_tirantes]"]');
        const tirantesQuantity = Math.max(0, parseInt(tirantesQuantityInput?.value) || 0);
        if (tirantesQuantity > 0) {
          items.push({
            id: tirantesYesInput.value,
            quantity: tirantesQuantity,
            properties: {
              _parentProductId: parentProductIdInput.value,
              _parentProductHandle: parentProductHandle.value,
              _tirantesProductId: tirantesProductInput.value
            }
          });
        }
      }

      return items;
    }

    handleVariantChange(e) {
      const details = e.target.closest('.product-groups__details');
      const priceLabel = details?.querySelector('.product-groups__price');
      if (priceLabel) {
        priceLabel.textContent = this.formatMoney(e.target.dataset.variantPrice);
      }
      this.checkAllSoldOut();
      this.updateTotalPrice();
    }

    async addMultipleItems(items) {
      this.setLoadingState(true);

      try {
        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ 
            items: items,
            sections: sectionsToBundle.join(",")
          })
        });

        if (!response.ok) throw new Error((await response.json()).description || 'Could not add to cart');
        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();

        this.setLoadingState(false);
        this.triggerCartEvents(await response.json(), cartContent);
        this.openCartDrawer();
      } catch (error) {
        this.setLoadingState(false);
        this.onCartError({ detail: { error: error.message } });
      }
    }

    setLoadingState(isLoading) {
      document.documentElement.dispatchEvent(new CustomEvent(
        isLoading ? 'theme:loading:start' : 'theme:loading:end',
        { bubbles: true }
      ));

      if (!this.submitButton) return;
      const spinner = this.submitButton.querySelector('.product-groups__spinner');
      if (isLoading) {
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.classList.add('button--loading');
        spinner?.classList.remove('hidden');
      } else {
        this.submitButton.removeAttribute('aria-busy');
        this.submitButton.classList.remove('button--loading');
        spinner?.classList.add('hidden');
      }
    }

    triggerCartEvents(responseJson, cartContent) {
      this.form.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: responseJson.items || [responseJson], cart: cartContent, onSuccessDo: 'force_open_drawer' }
      }));
      cartContent["sections"] = responseJson["sections"];
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart: cartContent }
      }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    openCartDrawer() {
      requestAnimationFrame(() => {
        const cartDrawer = document.querySelector('cart-drawer');
        cartDrawer?.show?.();
      });
    }

    updateTotalPrice() {
      if (!this.priceWrapper) return;

      let total = 0, compareTotal = 0, hasDiscount = false;

      this.querySelectorAll('.product-groups__item').forEach(item => {
        const variantInput = item.querySelector('.product-groups__variant-input:checked');
        const quantityInput = item.querySelector('.quantity-selector__input');
        if (!variantInput || !quantityInput) return;

        const price = parseInt(variantInput.dataset.variantPrice) || 0;
        const quantity = parseInt(quantityInput.value) || 0;
        const comparePrice = parseInt(variantInput.dataset.variantComparePrice) || price;

        total += price * quantity;
        compareTotal += comparePrice * quantity;
        if (comparePrice > price && quantity > 0) hasDiscount = true;
      });

      // Handle tirantes optional product
      const tirantesYesInput = this.querySelector('.product-groups__tirantes input[data-tirantes="yes"]:checked');
      if (tirantesYesInput) {
        const tirantesQuantityInput = this.querySelector('.product-groups__tirantes-quantity');
        const tirantesQuantity = Math.max(0, parseInt(tirantesQuantityInput?.value) || 0);
        const tirantesPrice = parseInt(tirantesYesInput.dataset.price) || 0;
        total += tirantesPrice * tirantesQuantity;
        compareTotal += tirantesPrice * tirantesQuantity;
      }

      const totalPriceLabel = this.priceWrapper.querySelector('.product-groups__total-price');
      if (totalPriceLabel) totalPriceLabel.textContent = this.formatMoney(total);

      const comparePriceLabel = this.priceWrapper.querySelector('.product-groups__compare-price');
      const discountBadge = this.priceWrapper.querySelector('.product-groups__discount-badge');
      const priceOriginal = this.priceWrapper.querySelector('.product-groups__price-original');

      if (hasDiscount && compareTotal > total && comparePriceLabel && discountBadge) {
        priceOriginal.style.display = 'flex';
        comparePriceLabel.textContent = this.formatMoney(compareTotal);
        discountBadge.textContent = `-${Math.round(((compareTotal - total) / compareTotal) * 100)}%`;
      } else if (priceOriginal) {
        priceOriginal.style.display = 'none';
      }
    }

    checkAllSoldOut() {
      if (!this.submitButton) return;
      const hasItems = Array.from(this.querySelectorAll('.quantity-selector__input'))
        .some(input => (parseInt(input.value) || 0) > 0);
      this.submitButton.disabled = !hasItems;
      this.submitButton.classList.toggle('button--disabled', !hasItems);
    }

    onCartError(event) {
      const error = event.detail?.error || 'An error occurred';
      if (this.errorWrapper) {
        if (this.errorMessage) this.errorMessage.textContent = error;
        this.errorWrapper.hidden = false;
      }
      this.announce(error, 'assertive');
    }

    announce(message, politeness = 'polite') {
      const el = document.createElement('div');
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', politeness);
      el.className = 'sr-only';
      el.textContent = message;
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 1000);
    }

    formatMoney(cents) {
      if (typeof theme !== 'undefined' && theme.Currency?.formatMoney) {
        return theme.Currency.formatMoney(cents, theme.moneyFormat);
      }
      return `${(cents / 100).toFixed(2).replace('.', ',')} ${this.currencySymbol}`;
    }
  });
}
